import React, { useEffect } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { ArrowRightIcon } from '@heroicons/react/16/solid'

import { Button, Input } from 'ui-v2'

interface Props {
  isSubmitting: boolean
  isVerifying: boolean
  onSubmit: (values: { [key: string]: string }) => void
  apiErrors?: { [key: string]: string }
}

const EmailVerification: React.FC<Props> = ({ onSubmit, isSubmitting, apiErrors, isVerifying }) => {
  const { values, errors, handleSubmit, setFieldValue, setErrors } = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: yup.object({
      email: yup.string().email('Incorrect e-mail address').required('Email is required'),
    }),
    validateOnChange: false,
    onSubmit: (values) => onSubmit(values),
  })

  useEffect(() => {
    setErrors(apiErrors || {})
  }, [apiErrors])

  return (
    <div className="relative w-full">
      {isVerifying && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-50 opacity-35 z-10">
          <span className="absolute top-1/2 left-1/2 w-12 h-12 inline-flex items-center justify-center -mt-6 -ml-6">
            <svg
              aria-hidden="true"
              className="w-10 h-10 text-gray-200 animate-spin dark:text-gray-600 fill-blue-800"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </span>
        </div>
      )}

      <div className="pb-4 mb-4 border-b border-secondary">
        <h1 className="text-3xl leading-9 font-normal text-gray-800 m-0 mb-2">Email verification</h1>
        <div className="text-sm leading-5 font-normal text-gray-600">
          Enter your email. A verification link will be sent to you.
        </div>
      </div>

      <div className="flex flex-col items-center gap-4">
        <Input
          value={values.email}
          label="Email"
          onChange={(value) => setFieldValue('email', value)}
          error={errors.email}
          className="w-full"
        />

        <Button onClick={() => handleSubmit()} isLoading={isSubmitting} className="mt-8 w-64" variant="primary">
          <span className="inline-flex items-center gap-2">
            Send email with link <ArrowRightIcon className="w-3" />
          </span>
        </Button>
      </div>
    </div>
  )
}

export default EmailVerification
